<template>
  <el-dialog
    :title="title"
    :visible.sync="visible"
    custom-class="rwd-dialog-large"
  >
    <el-form
      :model="formData"
      v-loading="loading"
      label-position="top"
      ref="form"
    >
      <el-row :gutter="20">
        <el-col :xs="24" :md="24">
          <remote-opt
            v-model="formData.bid"
            :base="base"
            name="branch"
            :labelArr="['bid', 'unhide_bid', 'name']"
            :fetchList="fetchBranchList"
            :srchFilter="branchFilter"
            keyName="bid"
            valueName="bid"
            @change="onChangeBranch"
            sort="bid"
            required
          >
          </remote-opt>
        </el-col>
        <el-col :xs="24" :md="24">
          <el-form-item
            v-if="branchData"
            :label="getLabelText(base, 'branch_detail')"
            class="branch-info"
          >
            <li><b>{{ getLabelText(base, 'legal_entity_name') }}</b>: {{ branchData.legal_entity_name }}</li>
            <li><b>{{ getLabelText(base, 'branch_name') }}</b>: {{ branchData.name }}</li>
            <li><b>{{ getLabelText(base, 'bid') }}</b>: {{ branchData.bid }}</li>
          </el-form-item>
        </el-col>
        <el-col :xs="24" :md="24">
          <std-opt
            v-model="formData.receipt.payment_mode"
            name="soaReceiptPaymentMode"
            prop="receipt.payment_mode"
            :hiddenValue="[0]"
            component="radio-button"
            required
          >
          </std-opt>
        </el-col>
        <el-col :xs="24" :md="12">
          <time-field
            v-model="formData.date"
            :base="base"
            name="date"
            prop="date"
            dataType="datetime"
            format="yyyy-MM-dd"
            valueFormat="yyyy-MM-dd"
            required
          >
          </time-field>
        </el-col>
        <el-col :xs="24" :md="12">
          <time-field
            v-model="formData.receipt.effective_date"
            :base="base"
            name="effective_date"
            prop="receipt.effective_date"
            dataType="datetime"
            format="yyyy-MM-dd"
            valueFormat="yyyy-MM-dd"
            required
          >
          </time-field>
        </el-col>
      </el-row>
      <el-row
        v-show="formData.bid"
        :gutter="20"
      >
        <el-col :xs="24" :md="12">
          <text-field
            v-model="formData.receipt_amount"
            :base="base"
            name="receipt_amount"
            dataType="price"
            :min="0"
            required
          >
            <template slot="prepend-input">{{ currencyCode }}</template>
          </text-field>
        </el-col>
        <el-col :xs="24" :md="12">
          <el-form-item
            :label="getLabelText(base, 'applied_amount')"
          >
            {{ $t('api.soa_receipt.fields.applied_amount.text', {
              input_amount: formData.input_amount || 0,
              receipt_amount: formData.receipt_amount || 0
            }) }}
          </el-form-item>
        </el-col>
        <el-col :xs="24" :md="24">
          <el-form-item
            :label="getLabelText(base, 'branch_doc_title')"
          >
            <el-table
              stripe
              v-loading="docLoading"
              :data="branchDocs"
              @selection-change="handleSelectionChange"
              ref="branchDocsTable"
            >
              <el-table-column
                align="center"
                type="selection"
                :width="55"
              >
              </el-table-column>
              <el-table-column
                align="center"
                prop="number"
                :label="getLabelText(base, 'number')"
              >
              </el-table-column>
              <el-table-column
                align="center"
                prop="date"
                :label="getLabelText(base, 'document_date')"
                :width="150"
              >
              </el-table-column>
              <std-opt
                align="left"
                :isForm="false"
                name="documentType"
                data="type"
                :hasFilter="false"
                width="130"
              >
              </std-opt>
              <text-field
                :isForm="false"
                :base="base"
                name="amount"
                data="total_amount"
                :isNumber="true"
              >
              </text-field>
              <text-field
                :isForm="false"
                :base="base"
                name="balance"
                :isNumber="true"
              >
              </text-field>
              <el-table-column
                align="center"
                :label="getLabelText(base, 'offset_amount')"
              >
                <template slot-scope="scope">
                  <el-input
                    v-model="scope.row.amount"
                    class="offset-amount"
                    @input="onChangeOffsetAmount"
                    :disabled="!isSelected(scope.row)"
                  >
                  </el-input>
                </template>
              </el-table-column>
            </el-table>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :gutter="20">
        <el-col :xs="24" :md="24">
          <text-field
            v-model="formData.subject"
            :base="base"
            name="subject"
          >
          </text-field>
        </el-col>
        <el-col :xs="24" :md="24">
          <text-field
            v-model="formData.remark"
            :base="base"
            name="remark"
          >
          </text-field>
        </el-col>
        <el-col :xs="24" :md="24">
          <text-field
            v-model="formData.memo"
            :base="base"
            name="memo"
          >
          </text-field>
        </el-col>
      </el-row>
    </el-form>
    <span slot="footer" class="dialog-footer">
      <el-button
        @click="visible = false"
      >
        {{ $t('api.cancelBtn') }}
      </el-button>
      <el-button type="primary"
        @click="save"
        :loading="saveLoading"
      >
        {{ title }}
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import { create, fetchBranchList, fetchDocList } from '@/api/chart/soa/receipt'
import { decimalFormat, parseFloatWithComma } from '@/utils/number'
import { getMap, getMapLabel, transText, getLabelText } from '@/utils/i18n'
import { branchFilter } from '@/utils/filter'
import { SOADocumentType } from '@/enums'

const defFormData = {
  date: null,
  bid: undefined,
  subject: '',
  remark: '',
  memo: '',
  receipt: {
    effective_date: null,
    payment_mode: 1
  }
}

export default {
  name: 'receipt-dialog',
  data () {
    return {
      base: 'soa_receipt',
      formData: Object.assign({}, defFormData),
      branchFilter: branchFilter,
      dueDate: '',
      docLoading: false,
      saveLoading: false,
      loading: false,
      branchData: null,
      branchDocs: null,
      docBalance: [],
      docBalanceSum: 0,
      applyDocs: [],
      resetReceiptAmount: false,
      visible: false
    }
  },
  props: {
    afterSaveOK: {
      type: Function,
      default: () => {}
    }
  },
  computed: {
    branchDocsQuery () {
      if (this.formData.bid) {
        return {
          filter: `{$and:[` +
            `{"bid":${this.formData.bid}},` +
            `{"type":{$in:[` +
              `${SOADocumentType.Invoice},` +
              `${SOADocumentType.DepositInvoice},` +
              `${SOADocumentType.DebitNote}` +
            `]}},` +
            `{"balance":{$gt:0}},` +
            `{"status":0}` +
            `]}`,
          limit: 20
        }
      }
      return ''
    },
    title () {
      return this.getLabelText(this.base, 'create')
    },
    currencyCode () {
      if (this.branchData && this.branchData.currency_code) {
        return this.branchData.currency_code
      }
      return '$'
    }
  },
  methods: {
    create,
    fetchBranchList,
    fetchDocList,
    decimalFormat,
    getMap,
    getMapLabel,
    transText,
    getLabelText,
    reset () {
      this.formData = JSON.parse(JSON.stringify(defFormData))
      let today = this.$moment().format('YYYY-MM-DD')
      this.formData.date = today
      this.formData.receipt.effective_date = today
      this.branchData = null
      if (this.$refs.form) {
        this.$refs.form.clearValidate()
      }
    },
    show () {
      this.visible = true
    },
    hide () {
      this.visible = false
    },
    save () {
      this.saveLoading = true
      this.$refs.form.validate(valid => {
        if (!valid) {
          this.saveLoading = false
          return
        }
        this.formData.create_apply_doc = []
        let docs = this.$refs.branchDocsTable.selection
        for (let i = 0; i < docs.length; ++i) {
          let doc = docs[i]
          this.formData.create_apply_doc.push({
            id: doc.id,
            amount: parseFloatWithComma(doc.amount) || 0
          })
        }

        this.create(this.formData).then(() => {
          this.$message({
            message: this.transText(this.base + '.addSuccess'),
            type: 'success',
            duration: 2000
          })
          this.hide()
          this.reset()
          this.afterSaveOK()
        }).catch((err) => {
          console.log(err)
        }).then(() => {
          this.saveLoading = false
        })
      })
    },
    handleSelectionChange (v) {
      if (this.resetReceiptAmount) {
        return
      }

      for (let i = 0; i < this.branchDocs.length; ++i) {
        let doc = this.branchDocs[i]
        let found = false
        for (let j = 0; j < v.length; ++j) {
          let sdoc = v[j]
          if (sdoc === doc) {
            if (doc.amount === undefined) {
              this.$set(doc, 'amount', 0)
            }
            found = true
            break
          }
        }
        if (!found) {
          this.$delete(doc, 'amount')
        }
      }
      this.calcInputAmount()
    },
    isSelected (row) {
      return this.$refs.branchDocsTable.selection.includes(row)
    },
    getSubsets (array, sum) {
      let fork = (i = 0, s = 0, t = []) => {
        if (s === sum) {
          result = t
          return
        }
        if (i === array.length || result.length > 0) {
          return
        }
        let sub = this.decimalFormat(s + array[i], 4)
        if (sub <= sum) {
          fork(i + 1, sub, t.concat({
            idx: i,
            value: array[i]
          }))
        }
        fork(i + 1, s, t)
      }

      let result = []
      fork()
      return result
    },
    resetDocAmount () {
      for (let i = 0; i < this.branchDocs.length; ++i) {
        let doc = this.branchDocs[i]
        this.$delete(doc, 'amount')
      }
      this.$refs.branchDocsTable.clearSelection()
    },
    calcInputAmount () {
      let total = 0
      let docs = this.$refs.branchDocsTable.selection
      for (let i = 0; i < docs.length; ++i) {
        let amount = parseFloatWithComma(docs[i].amount) || 0
        total = this.decimalFormat(total + amount, 4)
      }
      this.$set(this.formData, 'input_amount', total)
    },
    onChangeOffsetAmount (v) {
      if (this.resetReceiptAmount) {
        return
      }
      this.calcInputAmount()
    },
    onChangeBranch (v, opt) {
      this.formData.bid = v
      this.branchData = opt
      this.docLoading = true
      this.fetchDocList(this.branchDocsQuery).then((res) => {
        this.branchDocs = res.data
        this.docBalanceSum = 0
        this.docBalance = this.branchDocs.map((ele) => {
          this.docBalanceSum = this.decimalFormat(this.docBalanceSum + ele.balance, 4)
          return ele.balance
        })
      }).catch((err) => {
        console.log(err)
      }).then(() => {
        this.docLoading = false
      })
    }
  },
  created () {
    this.reset()
  },
  watch: {
    'formData.receipt_amount' (v) {
      this.resetReceiptAmount = true
      this.resetDocAmount()
      if (!v) {
        this.calcInputAmount()
        return
      }

      let parsed = parseFloatWithComma(v)
      if (parsed !== v) {
        this.$set(this.formData, 'receipt_amount', parsed)
        return
      }
      v = parsed

      let sets = []
      if (v <= this.docBalanceSum) {
        sets = this.getSubsets(this.docBalance, v)
      }
      if (sets.length > 0) {
        sets.forEach((e) => {
          let doc = this.branchDocs[e.idx]
          this.$set(doc, 'amount', e.value)
          this.$refs.branchDocsTable.toggleRowSelection(doc, true)
        })
      } else {
        let left = v
        for (let i = 0; i < this.branchDocs.length; ++i) {
          let doc = this.branchDocs[i]
          if (left === 0) {
            this.$delete(doc, 'amount')
            this.$refs.branchDocsTable.toggleRowSelection(doc, false)
            continue
          }

          let sub = this.decimalFormat(left - doc.balance, 4)
          if (sub >= 0) {
            this.$set(doc, 'amount', doc.balance)
            this.$nextTick(() => {
              this.$refs.branchDocsTable.toggleRowSelection(doc, true)
            })
            left = sub
          } else {
            this.$set(doc, 'amount', left)
            this.$refs.branchDocsTable.toggleRowSelection(doc, true)
            left = 0
          }
        }
      }
      this.$nextTick(() => {
        this.calcInputAmount()
        this.resetReceiptAmount = false
      })
    }
  }
}

</script>
<style rel="stylesheet/scss" lang="scss">
  .branch-info li {
    padding-left: 10px;
  }
  .offset-amount input {
    text-align: right;
  }
</style>
